import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";

export type LabelsetSourceInfoCardProps = BoxProps & {
    selectedFileDetails: TLabelsetInfo;
};
import Box from "@/ui/atoms/Box";
import {
    Dropdown,
    DropdownContent,
    DropdownSelect,
    Icon,
    IconBox,
} from "@proda-ai/ui-kit";
import Badge from "@/ui/atoms/Badge";
import Typography from "@/ui/atoms/Typography";
import React, { useRef } from "react";
import type { TLabelsetInfo } from "@/apis/odin/apiODIN";
import { formatStringDateTime } from "@/utils/dates";

export const testIds = {
    main: "LabelsetSourceInfoCard",
    otherDocBadge: "LabelsetSourceInfoCard-otherDocBadge",
    similarity: "LabelsetSourceInfoCard-similarity",
    otherDocDropdown: "LabelsetSourceInfoCard-otherDocDropdown",
    fileInfo: "LabelsetSourceInfoCard-fileInfo",
    otherDocDropdownOption: "LabelsetSourceInfoCard-otherDocDropdownOption",
    otherDocDropdownSection: "LabelsetSourceInfoCard-otherDocDropdownSection",
};
const LabelsetSourceInfoCard = (props: LabelsetSourceInfoCardProps) => {
    const { sx, className, selectedFileDetails, ...otherProps } = props;
    const [isDropdownVisible, setDropdownVisible] = React.useState(false);

    const dropdownTargetRef = useRef<HTMLDivElement>(null);

    return (
        <Box
            data-testid={testIds.main}
            className={clsx([testIds, className])}
            sx={{
                ...sx,
            }}
            {...otherProps}
        >
            <Box sx={{ paddingTop: "16px" }}>
                <Box
                    sx={{
                        position: "relative",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "var(--proda-white-l95)",
                        padding: "16px",
                        borderRadius: "4px",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                fontFamily: "Noto Sans",
                                fontWeight: "700",
                                fontSize: "12px",
                                lineHeight: "15px",
                            }}
                        >
                            Mapping Source Document
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            height: "60px",
                            width: "100%",
                            position: "relative",
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "var(--proda-white)",
                            padding: "8px",
                            borderRadius: "4px",
                            my: "8px",
                        }}
                    >
                        <IconBox mr={1}>
                            <Icon
                                icon="file-xls"
                                color="var(--proda-black-10)"
                            />
                        </IconBox>
                        <Box data-testid={testIds.fileInfo}>
                            <Box
                                sx={{
                                    fontFamily: "Noto Sans",
                                    fontWeight: "700",
                                    fontSize: "12px",
                                    lineHeight: "15px",
                                }}
                            >
                                {selectedFileDetails.fileName}
                            </Box>
                            <Box
                                sx={{
                                    fontFamily: "Noto Sans",
                                    fontSize: "10px",
                                    fontWeight: 400,
                                    lineHeight: "13px",
                                    color: "#62637F",
                                    mt: "2px",
                                }}
                            >
                                {`Completed at:${formatStringDateTime(selectedFileDetails.completedAt)}`}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ width: "100%", zIndex: 2 }}>
                        <Dropdown
                            dropdownRenderElement={dropdownTargetRef.current}
                            isShowing={
                                selectedFileDetails.matchingExtractionsCount > 0
                            }
                            onItemSelected={(): void => {}}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    width: "100%",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            fontFamily: "Noto Sans",
                                            fontWeight: "700",
                                            fontSize: "14px",
                                            lineHeight: "19px",
                                            cursor: "pointer",
                                        }}
                                        onClick={(): void => {
                                            setDropdownVisible(
                                                !isDropdownVisible,
                                            );
                                        }}
                                    >
                                        {selectedFileDetails.matchingExtractionsCount >
                                            0 && (
                                            <Badge
                                                backgroundColor={
                                                    isDropdownVisible
                                                        ? "proda-navy-blue"
                                                        : "disabled-bg"
                                                }
                                                borderColor={
                                                    isDropdownVisible
                                                        ? "proda-navy-blue"
                                                        : "disabled-bg"
                                                }
                                                data-testid={
                                                    testIds.otherDocBadge
                                                }
                                                leadingIcon={null}
                                            >
                                                <Typography
                                                    variant="body3"
                                                    sx={{
                                                        color: isDropdownVisible
                                                            ? "var(--proda-white)"
                                                            : "var(--proda-black)",
                                                    }}
                                                >
                                                    {`+${selectedFileDetails.matchingExtractionsCount} other documents`}
                                                </Typography>
                                                <IconBox
                                                    style={{
                                                        color: isDropdownVisible
                                                            ? "var(--proda-white)"
                                                            : "var(--proda-black)",

                                                        transform:
                                                            isDropdownVisible
                                                                ? "rotate(-180deg)"
                                                                : "rotate(0deg)",
                                                    }}
                                                    ml={1}
                                                >
                                                    <Icon
                                                        icon="caret-down"
                                                        size={12}
                                                    />
                                                </IconBox>
                                            </Badge>
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            fontFamily: "Noto Sans",
                                            fontWeight: "700",
                                            fontSize: "12px",
                                            lineHeight: "19px",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                        data-testid={testIds.similarity}
                                    >
                                        <IconBox m={0.5}>
                                            <Icon
                                                icon="circle"
                                                size={4}
                                                color={
                                                    selectedFileDetails.similarity ===
                                                    1
                                                        ? "var(--proda-green-120)"
                                                        : "var(--proda-yellow-120)"
                                                }
                                            />
                                        </IconBox>
                                        {(
                                            selectedFileDetails.similarity * 100
                                        ).toFixed(0)}
                                        %
                                    </Box>
                                </Box>
                            </Box>

                            {isDropdownVisible && (
                                <DropdownContent>
                                    <DropdownSelect
                                        labelledById="options"
                                        currentIndex={-1}
                                        role="listbox"
                                    >
                                        {selectedFileDetails?.matchingExtractions.map(
                                            (section, sectionIndex) => (
                                                <Box
                                                    key={sectionIndex}
                                                    data-testid={
                                                        testIds.otherDocDropdown
                                                    }
                                                >
                                                    <Box>
                                                        {
                                                            <Box
                                                                data-testid={
                                                                    testIds.otherDocDropdownSection
                                                                }
                                                                data-section-index={
                                                                    sectionIndex
                                                                }
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    textWrap:
                                                                        "wrap",
                                                                    color: "var(--proda-black-10)",
                                                                    fontSize:
                                                                        "12px",
                                                                    padding:
                                                                        "8px",
                                                                    backgroundColor:
                                                                        "var(--proda-white-l95)",
                                                                    borderTop:
                                                                        "1px solid var(--proda-white-150)",
                                                                }}
                                                            >
                                                                {`PROJECT:${section.dealName}`}
                                                            </Box>
                                                        }
                                                    </Box>
                                                    {section.files.map(
                                                        (file, fileIndex) => (
                                                            <Box
                                                                key={`${file}-${fileIndex}`}
                                                                data-testid={
                                                                    testIds.otherDocDropdownOption
                                                                }
                                                                data-file-index={`${sectionIndex}-${fileIndex}`}
                                                                sx={{
                                                                    textWrap:
                                                                        "wrap",
                                                                    fontSize:
                                                                        "12px",
                                                                    padding:
                                                                        "4px 8px",
                                                                    backgroundColor:
                                                                        "var(--proda-white)",
                                                                }}
                                                            >
                                                                {file}
                                                            </Box>
                                                        ),
                                                    )}
                                                </Box>
                                            ),
                                        )}
                                    </DropdownSelect>
                                </DropdownContent>
                            )}
                        </Dropdown>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default LabelsetSourceInfoCard;
